import React, { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Loader, Monitor, ShieldCheck } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { apiClient } from '@/utils/api'
import { Message } from '@/components/sharedDevice/Message'
import { StepUpCodeForm } from '@/components/sharedDevice/StepUpCodeForm'
import { parseServerTime, stepUpDecisionError } from '@/utils/sharedDevice'

const Card: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="min-h-screen flex items-start sm:items-center justify-center p-4 py-8">
    <div className="w-full max-w-lg bg-white rounded-2xl shadow-lg p-6 sm:p-8">
      <div className="flex items-center gap-3 mb-5">
        <div className="p-3 rounded-xl bg-indigo-100">
          <ShieldCheck className="w-6 h-6 text-indigo-700" aria-hidden="true" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
      </div>
      {children}
    </div>
  </div>
)

const NotParent: React.FC = () => (
  <Message tone="warn">Only a parent can approve requests from a shared device.</Message>
)

/** /step-up: a parent enters the S- code shown on a shared device. */
export const StepUpCodePage: React.FC = () => {
  const { user } = useAuth()
  return (
    <Card title="Approve a shared device">
      {!user?.is_parent ? (
        <NotParent />
      ) : (
        <>
          <p className="text-gray-600 mb-5">
            When a shared TV or tablet asks for a parent's approval, it shows a code that starts with S-. Enter it
            here to give that screen parent access for 10 minutes.
          </p>
          <StepUpCodeForm autoFocus />
          <Link to="/dashboard" className="inline-block mt-6 text-indigo-700 font-semibold hover:underline">
            Back to HousePoints
          </Link>
        </>
      )}
    </Card>
  )
}

interface StepUpPerson {
  id: string
  display_name: string | null
}

/** GET /api/device/step-up/:id/details (HP Phoenix 6.0.166). */
interface StepUpDetails {
  id: string
  status: 'pending' | 'approved' | 'denied' | 'expired'
  action: string | null
  device: { id: string; name: string | null; device_type: string | null }
  requested_for: StepUpPerson | null
  created_at: string
  expires_at: string
  decided_by: StepUpPerson | null
  decided_at: string | null
}

function timeLeft(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000))
  const m = Math.floor(total / 60)
  const sec = total % 60
  return `${m}:${String(sec).padStart(2, '0')}`
}

function clockTime(value: string | null): string {
  const d = parseServerTime(value)
  return d ? d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : ''
}

function personName(p: StepUpPerson | null, me: string | undefined, fallback: string): string {
  if (!p) return fallback
  if (me && p.id === me) return 'you'
  return p.display_name || fallback
}

function detailsError(status: number): string {
  if (status === 404) return 'This request was not found. It may belong to another family, or the link is incomplete.'
  if (status === 403) return 'Only a parent can see this request. Open it on your own phone or computer.'
  return 'Could not load this request. Check your connection and try again.'
}

/** /approve-step-up?id=...: see one request, then approve or deny it (push notification target). */
export const ApproveStepUpPage: React.FC = () => {
  const { user } = useAuth()
  const [params] = useSearchParams()
  const id = params.get('id') || ''
  const [details, setDetails] = useState<StepUpDetails | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState<'approve' | 'deny' | null>(null)
  const [result, setResult] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null)
  const [now, setNow] = useState(() => Date.now())

  const isParent = !!user?.is_parent

  const load = useCallback(async () => {
    if (!id || !isParent) return
    setLoading(true)
    setLoadError(null)
    try {
      setDetails(await apiClient.get(`/api/device/step-up/${encodeURIComponent(id)}/details`))
    } catch (err: any) {
      setLoadError(detailsError(err?.response?.status || 0))
    } finally {
      setLoading(false)
    }
  }, [id, isParent])

  useEffect(() => {
    load()
  }, [load])

  const pending = details?.status === 'pending'
  useEffect(() => {
    if (!pending) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [pending])

  const expiresAt = parseServerTime(details?.expires_at)?.getTime() ?? 0
  const timedOut = pending && expiresAt > 0 && now >= expiresAt

  const decide = async (decision: 'approve' | 'deny') => {
    setBusy(decision)
    setResult(null)
    try {
      await apiClient.post(`/api/device/step-up/${encodeURIComponent(id)}/${decision}`)
      setResult({
        tone: 'ok',
        text:
          decision === 'approve'
            ? 'Approved. The shared device has parent access for the next 10 minutes.'
            : 'Declined. The shared device will not get parent access.',
      })
    } catch (err: any) {
      setResult({ tone: 'error', text: stepUpDecisionError(err) })
    } finally {
      setBusy(null)
      load()
    }
  }

  const deviceName = details?.device?.name || 'A shared device'
  const asked = personName(details?.requested_for ?? null, user?.id, 'a parent')
  const decider = personName(details?.decided_by ?? null, user?.id, 'a parent')

  let outcome: { tone: 'ok' | 'warn'; text: string } | null = null
  if (details && !pending) {
    if (details.status === 'approved') {
      outcome = { tone: 'ok', text: `Approved by ${decider}${details.decided_at ? ` at ${clockTime(details.decided_at)}` : ''}.` }
    } else if (details.status === 'denied') {
      outcome = { tone: 'warn', text: `Declined by ${decider}${details.decided_at ? ` at ${clockTime(details.decided_at)}` : ''}.` }
    } else {
      outcome = { tone: 'warn', text: 'This request expired before anyone answered it. Ask for approval again on the shared device.' }
    }
  } else if (timedOut) {
    outcome = { tone: 'warn', text: 'This request expired before anyone answered it. Ask for approval again on the shared device.' }
  }

  return (
    <Card title="Parent approval request">
      {!isParent ? (
        <NotParent />
      ) : !id ? (
        <>
          <Message tone="warn">This link is missing its request. Enter the code shown on the device instead.</Message>
          <StepUpCodeForm />
        </>
      ) : loading && !details ? (
        <p className="flex items-center gap-3 text-gray-700" role="status">
          <Loader className="w-5 h-5 animate-spin" /> Loading the request...
        </p>
      ) : !details ? (
        <>
          <Message tone="error">{loadError || detailsError(0)}</Message>
          <p className="text-gray-700 mb-3">If the device is showing a code that starts with S-, you can enter it instead.</p>
          <StepUpCodeForm />
        </>
      ) : (
        <>
          <dl className="rounded-xl bg-gray-50 border border-gray-200 divide-y divide-gray-200 mb-5">
            <Row label="Device" value={<span className="inline-flex items-center gap-2"><Monitor className="w-4 h-4 text-gray-500" aria-hidden="true" />{deviceName}</span>} />
            <Row label="Wants to" value={details.action || 'Use parent tools'} />
            <Row label="Sent to" value={asked === 'you' ? 'You' : asked} />
            {pending && !timedOut && <Row label="Time left" value={<span className="font-mono">{timeLeft(expiresAt - now)}</span>} />}
          </dl>

          {result && <Message tone={result.tone}>{result.text}</Message>}
          {!result && outcome && <Message tone={outcome.tone}>{outcome.text}</Message>}

          {pending && !timedOut && (
            <>
              <p className="text-gray-700 mb-5">
                Approving gives {deviceName} parent access for 10 minutes, signed in as you
                {details.requested_for && details.requested_for.id !== user?.id ? ` (the request was sent to ${asked})` : ''}.
                Only approve if you or another parent asked for it at that screen.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => decide('approve')}
                  disabled={busy !== null}
                  className="flex-1 min-h-[52px] rounded-xl bg-indigo-600 text-white text-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 focus:outline-none focus:ring-4 focus:ring-indigo-300 inline-flex items-center justify-center gap-2"
                >
                  {busy === 'approve' && <Loader className="w-5 h-5 animate-spin" />}
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => decide('deny')}
                  disabled={busy !== null}
                  className="flex-1 min-h-[52px] rounded-xl border-2 border-gray-300 text-gray-900 text-lg font-semibold hover:bg-gray-50 disabled:opacity-50 focus:outline-none focus:ring-4 focus:ring-indigo-300 inline-flex items-center justify-center gap-2"
                >
                  {busy === 'deny' && <Loader className="w-5 h-5 animate-spin" />}
                  Deny
                </button>
              </div>
            </>
          )}
        </>
      )}
      <Link to="/dashboard" className="inline-block mt-6 text-indigo-700 font-semibold hover:underline">
        Back to HousePoints
      </Link>
    </Card>
  )
}

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between gap-4 px-4 py-3">
    <dt className="text-gray-500">{label}</dt>
    <dd className="text-gray-900 text-right">{value}</dd>
  </div>
)
